<template>
    <div class="image-upload" @click="triggerSelect">
        <!-- 自定义内容插槽 -->
        <slot>
            <div v-if="!value" class="upload-placeholder">
                <i class="el-icon-plus"></i>
            </div>
            <template v-else>
                <img :src="value" class="upload-preview" alt="preview"
                    :style="{ opacity: imgLoaded ? 1 : 0 }" @load="onPreviewLoad" @error="onPreviewError" />
                <!-- 图片加载中的 loading 遮罩（服务器在国外时较慢） -->
                <div v-if="!imgLoaded" class="upload-loading" v-loading="true"
                    element-loading-background="rgba(255,255,255,0.7)"></div>
            </template>
        </slot>

        <!-- 进度遮罩 -->
        <div v-if="uploading" class="upload-mask">
            <el-progress type="circle" :percentage="progress" :width="60" />
        </div>

        <!-- 删除图标 -->
        <i v-if="value && showRemove && !uploading" class="el-icon-close upload-remove" @click.stop="handleRemove"></i>

        <input ref="fileInput" type="file" class="file-input" accept="image/*" :multiple="multiple"
            @change="handleChange" />
    </div>
</template>

<script>
import { uploadImage } from '@/api'

export default {
    name: 'ImageUpload',
    props: {
        // v-model 绑定图片地址
        value: {
            type: String,
            default: ''
        },
        // 是否显示删除按钮
        showRemove: {
            type: Boolean,
            default: true
        },
        // 是否支持多图
        multiple: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            uploading: false,
            progress: 0,
            // 预览图是否已加载完成（用于 loading 遮罩与父组件禁用保存）
            imgLoaded: false
        }
    },
    watch: {
        // value 变化时重置图片加载状态（新图片需重新加载）
        value() {
            this.resetImgLoaded()
        }
    },
    mounted() {
        this.resetImgLoaded()
    },
    methods: {
        // 根据当前 value 决定初始加载状态
        resetImgLoaded() {
            this.imgLoaded = this.value ? false : true
        },
        // 预览图加载完成
        onPreviewLoad() {
            this.imgLoaded = true
            this.$emit('loaded', this.value)
        },
        // 预览图加载失败（即使失败也解除阻塞，避免按钮永久禁用）
        onPreviewError() {
            this.imgLoaded = true
            this.$emit('error', this.value)
        },
        triggerSelect() {
            if (this.uploading) return
            this.$refs.fileInput.click()
        },
        handleChange(e) {
            const files = e.target.files
            if (!files || files.length === 0) return
            this.upload(files[0])
            // 清空 input，保证同一文件可重复选择
            e.target.value = ''
        },
        async upload(file) {
            this.uploading = true
            this.progress = 0
            try {
                const res = await uploadImage(file, p => {
                    this.progress = p
                })
                // 接口返回结构：{ code, data: [{ path / url }] }
                const data = res && res.data
                let url = ''
                if (Array.isArray(data) && data.length > 0) {
                    url = data[0].path || data[0].url || data[0]
                } else if (data && typeof data === 'object') {
                    url = data.path || data.url || ''
                }
                // 仅当接口返回 localhost/127.0.0.1 时，才替换为线上域名；
                // 若接口已返回真实域名（如 https://app.nbumi-link.com），则原样保留
                if (url && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(url)) {
                    url = url.replace(/^https?:\/\/[^/]+/, 'https://yufabu.saizhuge.net')
                }
                if (url) {
                    // 新图片地址需重新加载，先置为未加载
                    this.imgLoaded = false
                    this.$emit('input', url)
                    this.$emit('success', url)
                } else {
                    this.$message.error('Upload failed')
                }
            } catch (err) {
                console.error('uploadImage failed', err)
                this.$message.error('Upload failed')
            } finally {
                this.uploading = false
                this.progress = 0
            }
        },
        handleRemove() {
            this.imgLoaded = true
            this.$emit('input', '')
            this.$emit('remove')
        }
    }
}
</script>

<style lang="scss" scoped>
.image-upload {
    position: relative;
    display: inline-block;
    cursor: pointer;

    .file-input {
        display: none;
    }

    .upload-placeholder,
    .upload-preview {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        object-fit: cover;
        border: 1px dashed #d9d9d9;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #8c939d;
        font-size: 24px;
    }

    .upload-preview {
        border-style: solid;
    }

    /* 预览图加载中的 loading 遮罩 */
    .upload-loading {
        position: absolute;
        top: 0;
        left: 0;
        width: 80px;
        height: 80px;
        border-radius: 50%;
        overflow: hidden;
    }

    .upload-mask {
        position: absolute;
        top: 0;
        left: 0;
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .upload-remove {
        position: absolute;
        top: -6px;
        right: -6px;
        width: 18px;
        height: 18px;
        line-height: 18px;
        text-align: center;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        font-size: 12px;
    }
}
</style>
